import React, {
  MouseEvent,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from 'core/components/Tooltip';
import Image from 'pages/Dashboard/components/Image';
import {
  UserDTO,
} from 'dtos';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import getToDoAvatarClass from 'pages/Dashboard/pages/Todos/avatarColors';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type Props = {
  collaborators?: UserDTO[];
  className?: string;
  max?: number;
  showAddButton?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  emptyState?: ReactNode;
};

type CollaboratorsContentProps = {
  collaborators: UserDTO[];
  emptyState: ReactNode;
  max: number;
  showAddButton: boolean;
};

type CollaboratorAvatarProps = {
  user: UserDTO;
  index: number;
  zIndex: number;
};

function CollaboratorAvatar({ user, index, zIndex }: CollaboratorAvatarProps) {
  const src = useUserProfilePicturePath(user.userId);
  return (
    <div
      className={clsx('relative', { '-ml-1.5': index > 0 })}
      style={{ zIndex }}
    >
      <Image
        size="sm"
        className={clsx('border border-white', getToDoAvatarClass(user.userId))}
        alt={getDisplayName(user.formattedNames)}
        src={src}
      >
        <span className="text-white">
          {user.formattedNames?.initials}
        </span>
      </Image>
    </div>
  );
}

function CollaboratorsContent({
  collaborators,
  emptyState,
  max,
  showAddButton,
}: CollaboratorsContentProps) {
  const hasCollaborators = collaborators.length > 0;
  const visibleCollaborators = collaborators.slice(0, max);
  const remainingCount = Math.max(collaborators.length - max, 0);

  const maxZindex = max + 2;

  return (
    <>
      {hasCollaborators
        ? (
          <div className="flex items-center">
            {visibleCollaborators.map((user, index) => (
              <CollaboratorAvatar
                key={user.userId}
                user={user}
                index={index}
                zIndex={maxZindex - index}
              />
            ))}
            {remainingCount > 0 && (
              <div
                className="relative -ml-1.5 flex h-6 min-w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-100 px-1 text-[10px] font-semibold text-gray-700"
                style={{ zIndex: 2 }}
              >
                +{remainingCount}
              </div>
            )}
          </div>
        )
        : emptyState}
      {showAddButton && (
        <div
          className={clsx(
            'relative flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-600',
            {
              '-ml-1': hasCollaborators,
            },
          )}
          style={{ zIndex: 1 }}
        >
          <AddIcon className="!h-3.5 !w-3.5" />
        </div>
      )}
    </>
  );
}

type CollaboratorsTooltipProps = {
  collaborators: UserDTO[];
};

function CollaboratorsTooltip({ collaborators }: CollaboratorsTooltipProps) {
  return (
    <div className="flex flex-col gap-1">
      {collaborators.length > 0
        ? collaborators.map((user) => (
          <span key={user.userId}>
            {getDisplayName(user.formattedNames, '--')}
          </span>
        ))
        : <span>No collaborators</span>}
    </div>
  );
}

export default function CollaboratorsSummary({
  collaborators = [],
  className,
  max = 3,
  showAddButton = false,
  disabled = false,
  onClick,
  emptyState = <span className="text-gray-400">--</span>,
}: Props) {
  return (
    <Tooltip
      title={<CollaboratorsTooltip collaborators={collaborators} />}
      isInteractive
    >
      {onClick
        ? (
          <button
            type="button"
            className={clsx(
              'flex items-center transition-[filter] hover:brightness-95 disabled:hover:brightness-100',
              className,
            )}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              onClick(event);
            }}
            disabled={disabled}
          >
            <CollaboratorsContent
              collaborators={collaborators}
              emptyState={emptyState}
              max={max}
              showAddButton={showAddButton}
            />
          </button>
        )
        : (
          <div className={clsx('flex items-center', className)}>
            <CollaboratorsContent
              collaborators={collaborators}
              emptyState={emptyState}
              max={max}
              showAddButton={showAddButton}
            />
          </div>
        )}
    </Tooltip>
  );
}
